/**
 * Weekly Intelligence Digest — Settings Page
 * - Toggle digest on/off (Pro+ only)
 * - Pick timezone (Monday 9am local delivery)
 * - Toggle CEO audio briefing
 * - Send a test digest now
 * - View last 10 sends
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Clock, Volume2, Send, CheckCircle2, AlertCircle, Sparkles, Loader2, Share2, Link2, Globe, Linkedin, Twitter, Briefcase, AtSign, BarChart3, Eye, MousePointerClick, Bell, TrendingUp, EyeOff, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A short curated list — the most common picks. Users with edge cases can request more later.
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'America/Denver', label: 'Denver (MT)' },
  { value: 'America/Chicago', label: 'Chicago (CT)' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris / Berlin' },
  { value: 'Europe/Athens', label: 'Athens' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

const INDUSTRIES = [
  { id: 'marketing', label: 'Marketing' },
  { id: 'technology', label: 'Technology' },
  { id: 'business', label: 'Business' },
  { id: 'ecommerce', label: 'E-commerce' },
  { id: 'health', label: 'Health' },
];

const DigestSettings = ({ onNavigate }) => {
  const [prefs, setPrefs] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [history, setHistory] = useState([]);
  const [publicPages, setPublicPages] = useState([]);
  const [growth, setGrowth] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [toast, setToast] = useState(null);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [editingUsername, setEditingUsername] = useState(false);
  const [pendingUnpub, setPendingUnpub] = useState(null);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchPrefs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/preferences`);
      setPrefs(data);
    } catch (e) {
      showToast('error', 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/history`);
      setHistory(data.history || []);
    } catch { /* silent */ }
  };

  const fetchPublicPages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/my-public-pages`);
      setPublicPages(data.pages || []);
    } catch { /* silent */ }
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/analytics`);
      setAnalytics(data);
    } catch { /* silent */ }
  };

  const fetchGrowth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/digest/analytics/subscriber-growth?days=30`);
      setGrowth(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchPrefs();
    fetchHistory();
    fetchPublicPages();
    fetchAnalytics();
    fetchGrowth();
  }, []);

  useEffect(() => {
    if (prefs?.username) setUsernameDraft(prefs.username);
  }, [prefs?.username]);

  const updatePrefs = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/digest/preferences`, patch);
      setPrefs((prev) => ({ ...prev, ...data.preferences }));
      showToast('success', 'Preferences saved');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Save failed';
      showToast('error', typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/digest/send-test`);
      const tail = data.public_url_path ? ` · public page: ${data.public_url_path}` : '';
      showToast('success', `Sent! Check your inbox (audio: ${data.audio_size_kb}KB)${tail}`);
      fetchHistory();
      fetchPublicPages();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Send failed';
      showToast('error', typeof detail === 'string' ? detail : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const publishNow = async () => {
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/digest/publish-now`);
      showToast('success', `Published! Public page: ${data.url_path}`);
      fetchPublicPages();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Publish failed';
      showToast('error', typeof detail === 'string' ? detail : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const notifySubscribersNow = async () => {
    setNotifying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/digest/notify-subscribers-now`);
      const tag = data.creator_username ? ` of @${data.creator_username}` : '';
      if (data.subscribers === 0) {
        showToast('success', `No subscribers${tag} yet — share your archive page to get followers`);
      } else {
        showToast('success', `Notified ${data.sent}/${data.subscribers} subscribers${tag}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Notification failed';
      showToast('error', typeof detail === 'string' ? detail : 'Notification failed');
    } finally {
      setNotifying(false);
      setShowNotifyConfirm(false);
    }
  };

  const requestNotify = () => {
    setShowNotifyConfirm(true);
  };

  const unpublishOne = async (slug) => {
    try {
      await axios.post(`${API_URL}/api/digest/digests/${slug}/unpublish`);
      showToast('success', 'Digest unpublished — no longer publicly visible');
      fetchPublicPages();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Unpublish failed');
    } finally {
      setPendingUnpub(null);
    }
  };

  const republishOne = async (slug) => {
    try {
      await axios.post(`${API_URL}/api/digest/digests/${slug}/republish`);
      showToast('success', 'Digest is public again');
      fetchPublicPages();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Republish failed');
    }
  };

  const copyPublicUrl = async (slug) => {
    const url = `${window.location.origin}/digest/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2200);
    } catch { /* clipboard unavailable */ }
  };

  const saveUsername = async () => {
    try {
      const { data } = await axios.put(`${API_URL}/api/digest/username`, { username: usernameDraft });
      setPrefs((p) => ({ ...p, username: data.username }));
      setEditingUsername(false);
      showToast('success', `Username set to @${data.username}`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to save username';
      showToast('error', typeof detail === 'string' ? detail : 'Failed');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Loading digest settings…
      </div>
    );
  }

  const eligible = prefs?.tier_eligible;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="digest-settings-page">
      {/* Re-run Awakening Ritual link (spec §Navigation #3) */}
      <button
        onClick={() => { if (window.iSupplyAIReRunAwakening) window.iSupplyAIReRunAwakening(); }}
        className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-br from-purple-900/30 via-gray-900/40 to-cyan-900/20 border border-purple-500/30 hover:border-purple-500/50 transition-all group"
        data-testid="rerun-awakening-link"
      >
        <span className="text-xl">⚡</span>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">Re-run Awakening Ritual</div>
          <div className="text-xs text-gray-400">Update your competitors or re-scan — gods will re-analyze your landscape</div>
        </div>
        <span className="text-purple-300 group-hover:translate-x-1 transition-transform">→</span>
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-blue-900/30 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <span className="text-2xl">📬</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Weekly Intelligence Digest</h1>
            <p className="text-sm text-gray-400 mt-0.5">Every Monday at 9:00am — your local time. 60-sec CEO briefing audio included.</p>
          </div>
          {eligible && (
            <span className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
              <Sparkles className="w-3 h-3" /> {prefs.tier?.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Free user upgrade card */}
      {!eligible && (
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-900/30 to-orange-900/20 p-6" data-testid="digest-upgrade-prompt">
          <div className="flex items-start gap-4">
            <span className="text-3xl">⭐</span>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-1">Upgrade to Pro to unlock your weekly Intelligence Digest</h3>
              <p className="text-sm text-gray-300 mb-4">
                Every Monday, get the 3 trending topics your competitors haven't touched, a Ready-to-Forge brief, and a
                60-second CEO audio briefing — perfect for your commute. Free users only see this teaser.
              </p>
              <button
                onClick={() => {
                  if (onNavigate) onNavigate('dashboard');
                  else window.location.href = '/';
                }}
                className="inline-block px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm cursor-pointer"
                data-testid="digest-see-pro-plans-btn"
              >
                See Pro plans →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences */}
      <div className={`rounded-2xl border border-gray-800 bg-gray-900/50 p-6 ${!eligible ? 'opacity-60 pointer-events-none' : ''}`}>
        <h2 className="text-base font-bold text-white mb-4">Preferences</h2>

        {/* Enabled toggle */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800" data-testid="digest-pref-enabled">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-purple-400" />
            <div>
              <div className="text-sm font-semibold text-white">Send me the weekly digest</div>
              <div className="text-xs text-gray-500">Email delivery — opt out anytime</div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs?.enabled}
              disabled={!eligible || saving}
              onChange={(e) => updatePrefs({ enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="digest-toggle-enabled"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500" />
          </label>
        </div>

        {/* Timezone */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800" data-testid="digest-pref-timezone">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-cyan-400" />
            <div>
              <div className="text-sm font-semibold text-white">Delivery timezone</div>
              <div className="text-xs text-gray-500">We send at 9:00am Monday in this timezone</div>
            </div>
          </div>
          <select
            value={prefs?.timezone || 'UTC'}
            disabled={!eligible || saving}
            onChange={(e) => updatePrefs({ timezone: e.target.value })}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500"
            data-testid="digest-select-timezone"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        {/* Audio toggle */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800" data-testid="digest-pref-audio">
          <div className="flex items-center gap-3">
            <Volume2 className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-sm font-semibold text-white">Include 60-second CEO audio briefing</div>
              <div className="text-xs text-gray-500">Embedded MP3 in the email — listen on your commute</div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs?.audio_enabled}
              disabled={!eligible || saving}
              onChange={(e) => updatePrefs({ audio_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="digest-toggle-audio"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500" />
          </label>
        </div>

        {/* Industry */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800" data-testid="digest-pref-industry">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-blue-400" />
            <div>
              <div className="text-sm font-semibold text-white">Your industry</div>
              <div className="text-xs text-gray-500">Feeds your digest's trending topics + appears in the public directory filter</div>
            </div>
          </div>
          <select
            value={prefs?.industry || 'marketing'}
            disabled={!eligible || saving}
            onChange={(e) => updatePrefs({ industry: e.target.value })}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
            data-testid="digest-select-industry"
          >
            {INDUSTRIES.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </div>

        {/* Public sharing toggle */}
        <div className="flex items-center justify-between py-3 border-b border-gray-800" data-testid="digest-pref-share">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                Auto-publish public landing page
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">NEW</span>
              </div>
              <div className="text-xs text-gray-500">
                {prefs?.share_enabled
                  ? <>Your digest will be visible at <code className="text-emerald-400">/digest/@{prefs?.username || 'your-username'}</code> and may appear in the <a href="/digest/featured" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">Featured Digest directory</a>.</>
                  : <>Your digest is private — only you receive it via email. Toggle ON to make it shareable.</>
                }
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs?.share_enabled}
              disabled={!eligible || saving}
              onChange={(e) => updatePrefs({ share_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="digest-toggle-share"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500" />
          </label>
        </div>

        {/* Username */}
        {prefs?.share_enabled && (
          <div className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid="digest-pref-username">
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <AtSign className="w-5 h-5 text-cyan-400" />
              <div>
                <div className="text-sm font-semibold text-white">Your digest username</div>
                <div className="text-xs text-gray-500">Your public URL slug — used in /digest/@username</div>
              </div>
            </div>
            {editingUsername ? (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">@</span>
                <input
                  type="text"
                  value={usernameDraft}
                  onChange={(e) => setUsernameDraft(e.target.value)}
                  placeholder="your-username"
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500 w-48"
                  data-testid="digest-username-input"
                />
                <button onClick={saveUsername} className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold" data-testid="digest-username-save">Save</button>
                <button onClick={() => { setEditingUsername(false); setUsernameDraft(prefs.username || ''); }} className="text-xs text-gray-500 hover:text-white">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <code className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-cyan-300">@{prefs.username || '—'}</code>
                <button onClick={() => setEditingUsername(true)} className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold" data-testid="digest-username-edit">Edit</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send test */}
      {eligible && (
        <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-white mb-1">Want to see it now?</div>
              <div className="text-xs text-gray-500">Sends a test digest to your account email — uses real ElevenLabs audio (~30 sec to render)</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={requestNotify}
                disabled={notifying}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="digest-notify-subscribers-btn"
              >
                {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                {notifying ? 'Notifying…' : 'Notify subscribers'}
              </button>
              <button
                onClick={publishNow}
                disabled={publishing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="digest-publish-now-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                {publishing ? 'Publishing…' : 'Publish public page only'}
              </button>
              <button
                onClick={sendTest}
                disabled={sending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="digest-send-test-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send test email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscriber Growth Analytics Card */}
      {eligible && growth && growth.username && (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 via-gray-900/60 to-gray-900/60 p-6" data-testid="digest-subscriber-growth-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-emerald-400" />
            <h2 className="text-base font-bold text-white">Subscriber Growth</h2>
            <span className="ml-auto text-xs text-gray-500">last 30 days · @{growth.username}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><Users className="w-3 h-3" /> Total subscribers</div>
              <div className="text-2xl font-bold text-emerald-300 mt-1" data-testid="growth-total-subs">{growth.total || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Added (30d)</div>
              <div className="text-2xl font-bold text-cyan-300 mt-1" data-testid="growth-added-30d">+{growth.added_in_window || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Growth rate</div>
              <div className="text-2xl font-bold text-amber-300 mt-1">
                {growth.total > 0 && (growth.total - growth.added_in_window) > 0
                  ? `+${Math.round((growth.added_in_window / Math.max(1, growth.total - growth.added_in_window)) * 100)}%`
                  : growth.added_in_window > 0 ? '+∞' : '0%'}
              </div>
            </div>
          </div>

          {/* Sparkline (simple SVG) */}
          {growth.series && growth.series.length > 0 && (() => {
            const w = 600;
            const h = 80;
            const max = Math.max(1, ...growth.series.map(s => s.cumulative));
            const stepX = w / Math.max(1, growth.series.length - 1);
            const path = growth.series.map((s, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${(h - (s.cumulative / max) * (h - 6) - 3).toFixed(1)}`).join(' ');
            return (
              <div className="rounded-lg bg-black/30 border border-gray-800 p-3 mb-3" data-testid="growth-sparkline">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={`${path} L${w},${h} L0,${h} Z`} fill="url(#growthGrad)" />
                  <path d={path} stroke="#34d399" strokeWidth="2" fill="none" />
                </svg>
                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                  <span>{growth.series[0]?.date}</span>
                  <span>{growth.series[growth.series.length - 1]?.date}</span>
                </div>
              </div>
            );
          })()}

          {/* Recent subscribers */}
          {growth.recent && growth.recent.length > 0 && (
            <div data-testid="growth-recent-subs">
              <div className="text-xs text-gray-400 mb-2 uppercase tracking-widest">Recent subscribers</div>
              <ul className="divide-y divide-gray-800">
                {growth.recent.slice(0, 5).map((r, i) => (
                  <li key={i} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-gray-300 font-mono">{r.masked_email}</span>
                    <span className="text-xs text-gray-500">{new Date(r.subscribed_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {growth.total === 0 && (
            <div className="text-sm text-gray-400 text-center py-4">
              No subscribers yet. Share your archive page to start growing your list:
              <a href={`/digest/@${growth.username}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-emerald-400 hover:text-emerald-300 font-mono">/digest/@{growth.username}</a>
            </div>
          )}
        </div>
      )}

      {/* Analytics Card */}
      {eligible && analytics && analytics.total_published > 0 && (
        <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-6" data-testid="digest-analytics-card">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <h2 className="text-base font-bold text-white">Digest Analytics</h2>
            <span className="ml-auto text-xs text-gray-500">{analytics.total_published} published · {analytics.subscriber_count || 0} subscribers</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><Eye className="w-3 h-3" /> Total views</div>
              <div className="text-xl font-bold text-purple-300 mt-1">{analytics.total_views || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><Share2 className="w-3 h-3" /> Total shares</div>
              <div className="text-xl font-bold text-cyan-300 mt-1">{analytics.total_shares || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> CTA clicks</div>
              <div className="text-xl font-bold text-amber-300 mt-1">{analytics.cta_clicks || 0}</div>
            </div>
            <div className="rounded-xl bg-gray-800/50 border border-gray-700 p-3">
              <div className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Signups attributed</div>
              <div className="text-xl font-bold text-emerald-300 mt-1">{analytics.signups_attributed || 0}</div>
            </div>
          </div>
          {/* Per-platform breakdown */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-1 rounded text-xs bg-[#0a66c2]/20 text-[#0a66c2] border border-[#0a66c2]/30">
              LinkedIn: {analytics.linkedin_shares || 0}
            </span>
            <span className="px-2 py-1 rounded text-xs bg-gray-800 text-gray-300 border border-gray-700">
              X: {analytics.twitter_shares || 0}
            </span>
            <span className="px-2 py-1 rounded text-xs bg-gray-800 text-gray-300 border border-gray-700">
              Copy: {analytics.copy_shares || 0}
            </span>
          </div>
          {analytics.top_digest && (
            <div className="rounded-lg bg-black/30 border border-gray-800 p-3 text-sm">
              <div className="text-xs text-gray-400 mb-1">Top-performing digest:</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white font-semibold truncate">@{analytics.top_digest.username} / {analytics.top_digest.week_slug}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">{analytics.top_digest.view_count || 0} views · {analytics.top_digest.share_count || 0} shares</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Published public pages */}
      {eligible && publicPages.length > 0 && (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 via-gray-900/60 to-gray-900/60 p-6" data-testid="digest-public-pages">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-4 h-4 text-emerald-400" />
            <h2 className="text-base font-bold text-white">Your shareable digest pages</h2>
            <span className="ml-auto text-xs text-gray-500">{publicPages.length} published</span>
          </div>
          <ul className="divide-y divide-gray-800">
            {publicPages.slice(0, 5).map((p) => {
              const path = (p.username && p.week_slug) ? `/digest/@${p.username}/${p.week_slug}` : `/digest/${p.slug}`;
              const url = `${window.location.origin}${path}`;
              const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
              const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent("My weekly Intelligence Pulse — 3 trends my competitors haven't touched.")}`;
              return (
                <li key={p.slug} className="py-3 flex flex-wrap items-center gap-2" data-testid={`digest-public-page-${p.slug}`}>
                  <div className="flex-1 min-w-[200px]">
                    <a href={path} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-300 hover:text-emerald-200 font-mono truncate block">
                      {path}
                    </a>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Published {new Date(p.published_at).toLocaleString()} · {p.view_count || 0} views · {p.share_count || 0} shares
                      {p.is_test && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-gray-800 text-gray-400 uppercase">test</span>}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(url);
                        setCopiedSlug(p.slug);
                        setTimeout(() => setCopiedSlug(null), 2200);
                      } catch { /* noop */ }
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700"
                    data-testid={`digest-copy-${p.slug}`}
                  >
                    {copiedSlug === p.slug ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Link2 className="w-3 h-3" />}
                    {copiedSlug === p.slug ? 'Copied' : 'Copy'}
                  </button>
                  <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-[#0a66c2] hover:bg-[#0d77e0] text-white">
                    <Linkedin className="w-3 h-3" />
                  </a>
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-black hover:bg-gray-800 text-white border border-gray-700">
                    <Twitter className="w-3 h-3" />
                  </a>
                  {p.is_public !== false ? (
                    <button
                      onClick={(e) => { e.preventDefault(); setPendingUnpub(p.slug); }}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-900"
                      data-testid={`digest-unpublish-${p.slug}`}
                      title="Make this single digest private"
                    >
                      <EyeOff className="w-3 h-3" /> Unpublish
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.preventDefault(); republishOne(p.slug); }}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-900/40 hover:bg-emerald-900/60 text-emerald-300 border border-emerald-900"
                      data-testid={`digest-republish-${p.slug}`}
                      title="Make this digest public again"
                    >
                      <Globe className="w-3 h-3" /> Republish
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* History */}
      {eligible && history.length > 0 && (
        <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-6" data-testid="digest-history">
          <h2 className="text-base font-bold text-white mb-3">Recent digests</h2>
          <ul className="divide-y divide-gray-800">
            {history.map((h, i) => (
              <li key={i} className="py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
                  <div>
                    <div className="text-gray-200">{new Date(h.sent_at).toLocaleString()}{h.subject_test && <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-gray-800 text-gray-400">TEST</span>}</div>
                    {!h.success && h.error && <div className="text-xs text-red-400 mt-0.5">{h.error}</div>}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {h.audio_kb > 0 ? `${h.audio_kb}KB audio` : 'no audio'}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unpublish confirm modal */}
      {pendingUnpub && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="digest-unpublish-confirm-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <EyeOff className="w-5 h-5 text-red-400" />
              <h3 className="text-base font-bold text-white">Unpublish this digest?</h3>
            </div>
            <p className="text-sm text-gray-300 mb-5">
              This single digest will become <strong className="text-white">private</strong> immediately. Existing subscribers won't be re-notified, and the public URL will return 404. Your other digests stay public. You can republish anytime.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingUnpub(null)}
                className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold border border-gray-700"
                data-testid="digest-unpublish-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => unpublishOne(pendingUnpub)}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold"
                data-testid="digest-unpublish-confirm-yes"
              >
                Make private
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify confirm modal */}
      {showNotifyConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="digest-notify-confirm-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-5 h-5 text-purple-400" />
              <h3 className="text-base font-bold text-white">Notify your subscribers?</h3>
            </div>
            <p className="text-sm text-gray-300 mb-5">
              This will send a real email <strong className="text-white">right now</strong> to every subscriber of your most recent published digest, with a link to read it. They'll receive it within seconds. If you have no subscribers yet, nothing happens.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNotifyConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold border border-gray-700"
                data-testid="digest-notify-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={notifySubscribersNow}
                disabled={notifying}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="digest-notify-confirm-send"
              >
                {notifying ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Send notification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium text-white shadow-xl z-50 ${
            toast.kind === 'success' ? 'bg-emerald-600' : 'bg-red-600'
          }`}
          data-testid="digest-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default DigestSettings;
